import authHeader from "../helpers/header";
import apiInstance from "./api";

/**
 * Report API Service
 * Standardized API service for all report-related endpoints
 */

// Upload Statistics
const getUploadedStats = async (days = 365) => {
  const res = await apiInstance.get(`reports/uploaded/stats?days=${days}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

// User Statistics
const getUsersStats = async () => {
  const res = await apiInstance.get(`reports/users/`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

const getTopUsersStats = async (limit = 5) => {
  const res = await apiInstance.get(`reports/users/statitics?limit=${limit}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

// Download Statistics
const getDownloadedStats = async (limit = 6) => {
  const res = await apiInstance.get(`reports/downloaded/?limit=${limit}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

// Recent Resources
const getRecentResources = async (status = "pending,draft", limit = 6) => {
  const res = await apiInstance.get(`reports/resources/recents?status=${status}&order_by=created_at&limit=${limit}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

const getRecentPublishedResources = async (limit = 6) => {
  const res = await apiInstance.get(`reports/resources/recents?status=active&order_by=updated_at&limit=${limit}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

// Search Statistics
const getRecentSearches = async () => {
  const res = await apiInstance.get(`reports/search/recents`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

// File Statistics
const getFileFormatStats = async () => {
  const res = await apiInstance.get(`reports/resources/fileformat`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

// Status Report for PieChart
const getResourceStatusReport = async () => {
  const res = await apiInstance.get(`reports/resources/status-report`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

// File Report
const getFileReport = async () => {
  const res = await apiInstance.get(`reports/files/report`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

// Creator-specific reports
const getCreatorUploadedStats = async (creatorId) => {
  const res = await apiInstance.get(`reports/uploaded/by_creator/${creatorId}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

const getCreatorDownloadedStats = async (creatorId) => {
  const res = await apiInstance.get(`reports/downloaded/by_creator/${creatorId}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

const getCreatorTotalStats = async (creatorId) => {
  const res = await apiInstance.get(`reports/total/by_creator/${creatorId}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

const getCreatorSummaryStats = async (creatorId) => {
  const res = await apiInstance.get(`reports/sumerize/by_creator/${creatorId}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

const getCreatorRecentResources = async (creatorId, params = {}) => {
  const { style = "", tag = "", limit = 10, is_picked = 0 } = params;
  const queryParams = new URLSearchParams({
    ...(style && { style }),
    ...(tag && { tag }),
    limit,
    is_picked
  }).toString();
  
  const res = await apiInstance.get(`reports/recents_uploaded/by_creator/${creatorId}?${queryParams}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

// Search and SEO Reports
const getTagsReport = async (params = {}) => {
  const { tag_name, limit = 10, offset = 0 } = params;
  const queryParams = new URLSearchParams({
    ...(tag_name && { tag_name }),
    limit,
    offset
  }).toString();
  
  const res = await apiInstance.get(`reports/tags?${queryParams}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

const getSeoUrlsReport = async (params = {}) => {
  const { q = "", status = "", canonical = "", meta = "", redirect = "", limit = 50, offset = 0 } = params;
  const queryParams = new URLSearchParams({
    ...(q && { q }),
    ...(status && { status }),
    ...(canonical && { canonical }),
    ...(meta && { meta }),
    ...(redirect && { redirect }),
    limit,
    offset
  }).toString();
  
  const res = await apiInstance.get(`reports/seo/urls?${queryParams}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

// Search Analytics
const getSearchVolume = async (period = "daily", days = null) => {
  let url = `reports/search-volume?period=${period}`;
  if (days) url += `&days=${days}`;
  const res = await apiInstance.get(url, {
    headers: authHeader(),
    withCredentials: true
  });
  return res.data;
};

const getTrendingQueries = async (days = 7, limit = 10) => {
  const res = await apiInstance.get(`reports/trending-queries?days=${days}&limit=${limit}`, {
    headers: authHeader(),
    withCredentials: true
  });
  return res.data;
};

const getSearchEffectiveness = async (days = 30, limit = 10) => {
  const res = await apiInstance.get(`reports/search-effectiveness?days=${days}&limit=${limit}`, {
    headers: authHeader(),
    withCredentials: true
  });
  return res.data;
};


const getSearchQueryReport = async (params = {}) => {
  const { range, limit = 10, offset = 0, order_by = "count", order = "desc" } = params;
  const queryParams = new URLSearchParams({
    ...(range && { range }),
    limit,
    offset,
    order_by,
    order
  }).toString();
  
  const res = await apiInstance.get(`reports/search?${queryParams}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

// Resource-specific reports
const getResourceTagsFilter = async (params = {}) => {
  const { count = "", limit = 50, offset = 0 } = params;
  const queryParams = new URLSearchParams({
    ...(count && { count }),
    limit,
    offset
  }).toString();
  
  const res = await apiInstance.get(`reports/tags/filters?${queryParams}`, { 
    headers: authHeader(),
    withCredentials: true 
  });
  return res.data;
};

const reportAPI = {
  // Upload Statistics
  getUploadedStats,
  
  // User Statistics
  getUsersStats,
  getTopUsersStats,
  
  // Download Statistics
  getDownloadedStats,
  
  // Recent Resources
  getRecentResources,
  getRecentPublishedResources,
  
  // Search Statistics
  getRecentSearches,
  getSearchVolume,
  getTrendingQueries,
  getSearchEffectiveness,
  
  // File Statistics
  getFileFormatStats,
  getFileReport,
  
  // Status Reports
  getResourceStatusReport,
  
  // Creator-specific reports
  getCreatorUploadedStats,
  getCreatorDownloadedStats,
  getCreatorTotalStats,
  getCreatorSummaryStats,
  getCreatorRecentResources,
  
  // Search and SEO Reports
  getTagsReport,
  getSeoUrlsReport,
  getSearchQueryReport,
  getResourceTagsFilter,
};

export default reportAPI;
